import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import {
  ADMIN_TAB_LOCKS_EVENT,
  getLockedTabPaths,
  SIDEBAR_TABS,
} from "../utils/adminTabAccess";
import { clearAuth, getDisplayName, getStoredUser } from "../utils/auth";
import BrandLogo from "./BrandLogo";
import "./Sidebar.css";

/** Visual grouping only — paths stay the same as SIDEBAR_TABS. */
const NAV_SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    paths: ["/dashboard"],
  },
  {
    id: "access",
    label: "Access",
    paths: ["/roles", "/permissions", "/extension-license"],
  },
  {
    id: "people",
    label: "People",
    paths: ["/Agents", "/Clients"],
  },
  {
    id: "vfs",
    label: "VFS",
    paths: ["/booking-details"],
  },
  {
    id: "tls",
    label: "TLS",
    paths: ["/tls-form"],
  },
  {
    id: "bookings",
    label: "Bookings",
    paths: ["/AssistedBookings", "/available-slots", "/BookingStatusPage"],
  },
];

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

const Sidebar = () => {
  const [lockedPaths, setLockedPaths] = useState(() => getLockedTabPaths());
  const [openSection, setOpenSection] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userLabel, setUserLabel] = useState(() => getDisplayName());
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);

  useEffect(() => {
    const syncLockedPaths = () => setLockedPaths(getLockedTabPaths());
    const syncUser = () => setUserLabel(getDisplayName(getStoredUser()));

    window.addEventListener(ADMIN_TAB_LOCKS_EVENT, syncLockedPaths);
    window.addEventListener("storage", syncLockedPaths);
    window.addEventListener("storage", syncUser);

    return () => {
      window.removeEventListener(ADMIN_TAB_LOCKS_EVENT, syncLockedPaths);
      window.removeEventListener("storage", syncLockedPaths);
      window.removeEventListener("storage", syncUser);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenSection(null);
  }, [location.pathname]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!navRef.current?.contains(event.target)) {
        setOpenSection(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const unlockedTabs = useMemo(() => {
    const locked = new Set(lockedPaths);
    return SIDEBAR_TABS.filter(
      (item) => !locked.has(item.path.toLowerCase())
    );
  }, [lockedPaths]);

  const sections = useMemo(() => {
    return NAV_SECTIONS.map((section) => {
      const items = section.paths
        .map((path) => unlockedTabs.find((tab) => tab.path === path))
        .filter(Boolean);
      return { ...section, items };
    }).filter((section) => section.items.length > 0);
  }, [unlockedTabs]);

  const handleLogout = () => {
    clearAuth();
    navigate("/", { replace: true });
  };

  const isPathActive = (path) =>
    location.pathname.toLowerCase() === String(path).toLowerCase();

  const isSectionActive = (section) =>
    section.items.some((item) => isPathActive(item.path));

  const toggleSection = (sectionId) => {
    setOpenSection((current) => (current === sectionId ? null : sectionId));
  };

  return (
    <>
      <header className="vp-topbar">
        <BrandLogo to="/dashboard" className="vp-topbar-brand" height={42} />

        <nav className="vp-topbar-nav" ref={navRef} aria-label="Main">
          {sections.map((section, index) => {
            const active = isSectionActive(section);
            const isOpen = openSection === section.id;

            const trigger =
              section.items.length === 1 ? (
                <Link
                  to={section.items[0].path}
                  className={`vp-topbar-trigger${
                    isPathActive(section.items[0].path) ? " is-active" : ""
                  }`}
                >
                  {section.label}
                </Link>
              ) : (
                <>
                  <button
                    type="button"
                    className={`vp-topbar-trigger${active ? " is-active" : ""}`}
                    aria-expanded={isOpen}
                    aria-haspopup="menu"
                    onClick={() => toggleSection(section.id)}
                  >
                    <span>{section.label}</span>
                    <FaChevronDown className="vp-topbar-caret" aria-hidden="true" />
                  </button>

                  <div className="vp-topbar-dropdown" role="menu">
                    {section.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        role="menuitem"
                        className={`vp-topbar-dropdown-link${
                          isPathActive(item.path) ? " is-active" : ""
                        }`}
                        onClick={() => setOpenSection(null)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </>
              );

            return (
              <React.Fragment key={section.id}>
                {index > 0 ? <span className="vp-topbar-sep" aria-hidden="true" /> : null}
                <div
                  className={`vp-topbar-item${isOpen ? " is-open" : ""}`}
                  onMouseEnter={
                    section.items.length > 1
                      ? () => setOpenSection(section.id)
                      : undefined
                  }
                  onMouseLeave={
                    section.items.length > 1
                      ? () =>
                          setOpenSection((current) =>
                            current === section.id ? null : current
                          )
                      : undefined
                  }
                >
                  {trigger}
                </div>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="vp-topbar-actions">
          <div className="vp-topbar-user">
            <span className="vp-topbar-avatar" aria-hidden="true">
              {initialsFromName(userLabel)}
            </span>
            <span className="vp-topbar-user-name">{userLabel}</span>
          </div>

          <button
            type="button"
            className="vp-topbar-logout"
            onClick={handleLogout}
            title="Logout"
          >
            Logout
          </button>

          <button
            type="button"
            className="vp-topbar-menu-btn"
            onClick={() => setMobileOpen((value) => !value)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      {mobileOpen && (
        <div className="vp-topbar-mobile">
          {sections.map((section) => (
            <div className="vp-topbar-mobile-section" key={section.id}>
              <div className="vp-topbar-mobile-title">{section.label}</div>
              {section.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`vp-topbar-mobile-link${
                    isPathActive(item.path) ? " is-active" : ""
                  }`}
                  onClick={() => setMobileOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default Sidebar;
